/**
 * The two boards and their geometry. A grid is a string of `size²` characters, row by row: `"0"` is
 * an empty cell, `"1"`…`"9"` (`"1"`…`"6"` on the 6×6) a digit. Strings, because a grid is stored,
 * compared and sent as one — the bank, the api's saved game and the finished grid it checks.
 */

/** The two sizes the game offers. */
export const SUDOKU_SIZES = [6, 9] as const;
export type SudokuSize = (typeof SUDOKU_SIZES)[number];

/** The three levels, easiest first. Their rubric is in the package README. */
export const SUDOKU_LEVELS = ["easy", "medium", "hard"] as const;
export type SudokuLevel = (typeof SUDOKU_LEVELS)[number];

export function isSudokuSize(value: unknown): value is SudokuSize {
  return value === 6 || value === 9;
}

export function isSudokuLevel(value: unknown): value is SudokuLevel {
  return value === "easy" || value === "medium" || value === "hard";
}

/** A box's shape: 3 × 3 on the 9×9; on the 6×6, 2 rows × 3 columns. */
export interface BoxShape {
  readonly rows: number;
  readonly cols: number;
}

export function boxShape(size: SudokuSize): BoxShape {
  return size === 9 ? { rows: 3, cols: 3 } : { rows: 2, cols: 3 };
}

/** Everything about one size that the solver, the grader and the game ask over and over. */
export interface Geometry {
  readonly size: SudokuSize;
  readonly cells: number;
  readonly box: BoxShape;
  /** All `3 × size` units (rows, then columns, then boxes), each its cells in reading order. */
  readonly units: readonly (readonly number[])[];
  /** Per cell: its row, column and box unit indexes (into `units`). */
  readonly unitsOf: readonly (readonly [number, number, number])[];
  /** Per cell: every OTHER cell sharing its row, column or box. */
  readonly peers: readonly (readonly number[])[];
  /** Per cell: its box's index (0…size−1, reading order). */
  readonly boxOf: readonly number[];
  /** A mask with one bit per digit: bit `d − 1` is digit `d`. */
  readonly allDigits: number;
}

function build(size: SudokuSize): Geometry {
  const box = boxShape(size);
  const cells = size * size;
  const boxesPerBand = size / box.cols;
  const boxOf: number[] = [];
  for (let i = 0; i < cells; i++) {
    const r = Math.floor(i / size);
    const c = i % size;
    boxOf.push(Math.floor(r / box.rows) * boxesPerBand + Math.floor(c / box.cols));
  }
  const units: number[][] = [];
  for (let r = 0; r < size; r++) units.push(Array.from({ length: size }, (_, c) => r * size + c));
  for (let c = 0; c < size; c++) units.push(Array.from({ length: size }, (_, r) => r * size + c));
  for (let b = 0; b < size; b++) {
    const unit: number[] = [];
    for (let i = 0; i < cells; i++) if (boxOf[i] === b) unit.push(i);
    units.push(unit);
  }
  const unitsOf: [number, number, number][] = [];
  const peers: number[][] = [];
  for (let i = 0; i < cells; i++) {
    const r = Math.floor(i / size);
    const c = i % size;
    const b = boxOf[i] ?? 0;
    unitsOf.push([r, size + c, 2 * size + b]);
    const set = new Set<number>();
    for (const u of [r, size + c, 2 * size + b]) for (const j of units[u] ?? []) set.add(j);
    set.delete(i);
    peers.push([...set].sort((a, z) => a - z));
  }
  return { size, cells, box, units, unitsOf, peers, boxOf, allDigits: (1 << size) - 1 };
}

const GEOMETRY: Readonly<Record<SudokuSize, Geometry>> = { 6: build(6), 9: build(9) };

export function geometry(size: SudokuSize): Geometry {
  return GEOMETRY[size];
}

/** The digit in a grid string at `cell` (0 for an empty cell or anything that is not a digit). */
export function digitAt(grid: string, cell: number): number {
  const code = grid.charCodeAt(cell) - 48;
  return code >= 1 && code <= 9 ? code : 0;
}

/** The grid as digits (0 = empty). */
export function toDigits(grid: string): number[] {
  return Array.from({ length: grid.length }, (_, i) => digitAt(grid, i));
}

export function fromDigits(digits: readonly number[]): string {
  return digits.map((d) => String(d)).join("");
}

/**
 * Is `grid` shaped like a grid of this size — `size²` characters, each `"0"` or a digit of the size?
 * Says nothing of whether the digits clash; {@link clashFree} does.
 */
export function isGridShape(grid: string, size: SudokuSize): boolean {
  if (grid.length !== size * size) return false;
  for (let i = 0; i < grid.length; i++) {
    const code = grid.charCodeAt(i) - 48;
    if (code < 0 || code > size) return false;
  }
  return true;
}

/** No digit twice in any row, column or box (empty cells clash with nothing). */
export function clashFree(grid: string, size: SudokuSize): boolean {
  const g = geometry(size);
  for (const unit of g.units) {
    let seen = 0;
    for (const cell of unit) {
      const d = digitAt(grid, cell);
      if (d === 0) continue;
      const bit = 1 << (d - 1);
      if (seen & bit) return false;
      seen |= bit;
    }
  }
  return true;
}

/** A complete, clash-free grid of this size: what a solution is. */
export function isCompleteGrid(grid: string, size: SudokuSize): boolean {
  if (!isGridShape(grid, size) || grid.includes("0")) return false;
  return clashFree(grid, size);
}

/** The number of givens (non-empty cells). */
export function givenCount(grid: string): number {
  let n = 0;
  for (let i = 0; i < grid.length; i++) if (grid[i] !== "0") n++;
  return n;
}

/** The number of set bits in a digit mask. */
export function popcount(mask: number): number {
  let n = 0;
  let m = mask;
  while (m) {
    m &= m - 1;
    n++;
  }
  return n;
}

/** The lowest digit in a mask (1-based), 0 for an empty mask. */
export function lowestDigit(mask: number): number {
  if (mask === 0) return 0;
  return 31 - Math.clz32(mask & -mask) + 1;
}
